import React, { ReactElement } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { CopyrightFooter } from 'modules/common';
import { ReactComponent as ZTLoginPageLogo } from './images/ZT_Logo_Horizontal.svg';
import img from './images/Marketing_Image.png';

const useStyles = makeStyles((theme) => ({
  image: {
    height: theme.spacing(60),
    width: theme.spacing(60)
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    height: '100%',
    marginBottom: theme.spacing(0)
  },
  gridContent: {
    marginRight: theme.spacing(3)
  },
  logo: {
    marginBottom: theme.spacing(2)
  },
  button: {
    margin: theme.spacing(5, 0, 6, 0),
    color: theme.palette.common.white,
    padding: theme.spacing(1.1, 3.5),
    borderRadius: theme.spacing(1)
  },
  circularProgress: {
    margin: theme.spacing(5, 0, 6, 0)
  },
  divider: {
    width: '90%',
    color: theme.palette.grey[700],
    borderBottomWidth: theme.spacing(0.2)
  },
  bodyMessage: {
    fontWeight: 600,
    marginTop: theme.spacing(1)
  },
  text: {
    whiteSpace: 'nowrap',
    ...theme.typography.body1
  },
  bodyDiv: {
    marginBottom: theme.spacing(3)
  },
  repliconFooter: {
    color: theme.palette.text.primary
  },
  bottomBar: {
    display: 'flex',
    backgroundColor: '#110434',
    height: theme.spacing(7.5),
    justifyContent: 'space-around',
    alignItems: 'center',
    color: theme.palette.common.white,
    padding: theme.spacing(0, 18.5),
    position: 'fixed',
    bottom: theme.spacing(0),
    width: '100%'
  },
  textGridContainer: {
    marginTop: theme.spacing(12)
  },
  subText: {
    color: theme.palette.text.primary
  }
}));

type LoginUiProps = {
  disable: boolean;
  onClick: () => void;
};

const LoginUi = ({
  onClick,
  disable
}: LoginUiProps): ReactElement<LoginUiProps> => {
  const classes = useStyles();
  return (
    <Box>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        className={classes.container}
      >
        <Grid item xs={1.5}></Grid>
        <Grid
          item
          xs={5}
          container
          alignItems="flex-start"
          direction="column"
          justifyContent="center"
          className={classes.textGridContainer}
        >
          <div className={classes.logo} data-testid="zt_logo">
            <ZTLoginPageLogo />
          </div>
          <div className={classes.subText}>
            <Typography variant="h4" data-testid="LoginMessage1">
              <FormattedMessage id="loginPage.LoginMessage" />
            </Typography>
            <Typography variant="h4" data-testid="LoginMessage2">
              <FormattedMessage id="loginPage.LoginMessage1" />
            </Typography>
          </div>

          {disable ? (
            <CircularProgress
              data-testid="login-progress"
              className={classes.circularProgress}
            />
          ) : (
            <Button
              variant="contained"
              size="medium"
              data-testid="login-button"
              className={classes.button}
              onClick={onClick}
              disabled={disable}
            >
              <Typography variant="body1">
                <FormattedMessage id="signin" />
              </Typography>
            </Button>
          )}
          <Divider className={classes.divider} />
          <div data-testid="bodyMessage">
            <Typography variant="body1" className={classes.bodyMessage}>
              <FormattedMessage id="loginPage.bodyMessage" />
            </Typography>
            <Grid container item direction="row" className={classes.bodyDiv}>
              <Grid item xs={8}>
                <Typography variant="body1" className={classes.text}>
                  <FormattedMessage id="loginPage.northAmerica" />
                </Typography>
                <Typography variant="body1" className={classes.text}>
                  <FormattedMessage id="loginPage.OtherCountries" />
                </Typography>
                <Typography variant="body1" className={classes.text}>
                  <FormattedMessage id="loginPage.emailUsAT" />
                </Typography>
              </Grid>
              <Grid item xs={4} justifyContent="center">
                <Typography className={classes.text}>
                  <FormattedMessage id="loginPage.northPhone" />
                </Typography>
                <Typography className={classes.text}>
                  <FormattedMessage id="loginPage.otherPhone" />
                </Typography>
                <Link
                  href="mailto:support@replicon.com"
                  className={classes.text}
                >
                  <FormattedMessage id="loginPage.email" />
                </Link>
              </Grid>
            </Grid>
            <CopyrightFooter className={classes.repliconFooter} />
          </div>
        </Grid>
        <Grid
          item
          container
          xs={5.5}
          alignItems="center"
          justifyContent="flex-start"
        >
          <img
            src={img}
            alt="Logo"
            className={classes.image}
            data-testid="image"
          />
        </Grid>
      </Grid>
      <div className={classes.bottomBar} data-testid="footer">
        <Typography variant="body2">
          <FormattedMessage id="loginPage.footerMessage1" />
        </Typography>
        <Typography variant="body2">|</Typography>
        <Typography variant="body2">
          <FormattedMessage id="loginPage.footerMessage2" />
        </Typography>
        <Typography variant="body2">|</Typography>
        <Typography variant="body2">
          <FormattedMessage id="loginPage.footerMessage3" />
        </Typography>
      </div>
    </Box>
  );
};

export default LoginUi;
